import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FileText, Clock, CheckCircle, AlertTriangle,
  DollarSign, TrendingUp, ArrowRight, Download
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#0A2463', '#007AFF', '#16A34A', '#F59E0B', '#E11D48'];

function KPICard({ title, value, icon: Icon, color, sub }) {
  return (
    <Card className="bg-white border border-slate-200 rounded-sm shadow-none hover:border-slate-300 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{title}</p>
            <p className="font-mono text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
          </div>
          <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${color}`}>
            <Icon className="h-4 w-4 text-white stroke-[1.5px]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const [kpis, setKpis] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [confidence, setConfidence] = useState([]);
  const [attention, setAttention] = useState([]);
  const [costByType, setCostByType] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [k, p, c, a, ct] = await Promise.all([
          api.get('/dashboard/kpis'),
          api.get('/dashboard/pipeline'),
          api.get('/dashboard/confidence-distribution'),
          api.get('/dashboard/attention-required'),
          api.get('/dashboard/cost-by-type'),
        ]);
        setKpis(k.data);
        setPipeline(p.data);
        setConfidence(c.data);
        setAttention(a.data);
        setCostByType(ct.data);
      } catch (e) {
        console.error('Dashboard load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Skeleton className="h-64 lg:col-span-8" />
          <Skeleton className="h-64 lg:col-span-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Invoice reconciliation pipeline overview</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          data-testid="csv-export-button"
          className="rounded-sm text-xs border-slate-200 hover:border-slate-300"
          onClick={async () => {
            try {
              const res = await api.get('/invoices/export', { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a');
              a.href = url;
              a.download = `molecuai_invoices_${new Date().toISOString().slice(0,10)}.csv`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
            } catch (e) { console.error('Export failed:', e); }
          }}
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />Export CSV
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
        <KPICard title="Total Invoices" value={kpis?.total_invoices || 0} icon={FileText} color="bg-[#0A2463]" sub={`$${(kpis?.total_value || 0).toLocaleString()}`} />
        <KPICard title="Pending Approval" value={kpis?.pending_approval || 0} icon={Clock} color="bg-[#F59E0B]" sub="Awaiting review" />
        <KPICard title="Processing Rate" value={`${kpis?.processing_rate || 0}%`} icon={TrendingUp} color="bg-[#16A34A]" sub={`${kpis?.matched_invoices || 0} matched`} />
        <KPICard title="Error Rate" value={`${kpis?.error_rate || 0}%`} icon={AlertTriangle} color="bg-[#E11D48]" sub={`${kpis?.error_invoices || 0} errors`} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Pipeline Funnel */}
        <Card className="lg:col-span-8 bg-white border border-slate-200 rounded-sm shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-800">Pipeline Funnel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pipeline} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis dataKey="stage" type="category" tick={{ fontSize: 11, fill: '#64748b' }} width={80} />
                  <RechartsTooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} />
                  <Bar dataKey="count" fill="#0A2463" radius={[0, 2, 2, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Confidence Distribution */}
        <Card className="lg:col-span-4 bg-white border border-slate-200 rounded-sm shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-800">Confidence Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={confidence} dataKey="count" nameKey="level" cx="50%" cy="50%" outerRadius={80} strokeWidth={1} stroke="#fff">
                    {confidence.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <RechartsTooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-2 mt-2 justify-center">
              {confidence.map((c, i) => (
                <div key={c.level} className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">{c.level}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost by Type */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-800">Cost Distribution by Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={costByType} margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="type" tick={{ fontSize: 10, fill: '#64748b' }} angle={-30} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                <RechartsTooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} formatter={(v) => `$${v.toLocaleString()}`} />
                <Bar dataKey="total" fill="#007AFF" radius={[2, 2, 0, 0]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Attention Required Table */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-slate-800">Requiring Attention</CardTitle>
          <Badge variant="outline" className="rounded-sm text-[10px]">{attention.length} items</Badge>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="attention-table">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Invoice</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Vendor</th>
                  <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Amount</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Status</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-3">Confidence</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {attention.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/invoices/${inv.id}`)} data-testid={`attention-row-${inv.invoice_number}`}>
                    <td className="px-5 py-3 font-mono text-xs text-slate-900">{inv.invoice_number}</td>
                    <td className="px-5 py-3 text-slate-600">{inv.vendor_name}</td>
                    <td className="px-5 py-3 text-right font-mono text-slate-900">{inv.currency} {parseFloat(inv.total_amount || 0).toLocaleString()}</td>
                    <td className="px-5 py-3">
                      <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider border-amber-300 text-amber-700 bg-amber-50">
                        {inv.status?.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant="outline" className={`rounded-sm text-[10px] uppercase tracking-wider ${
                        inv.confidence === 'very_low' || inv.confidence === 'low' ? 'border-red-300 text-red-700 bg-red-50' :
                        inv.confidence === 'medium' ? 'border-amber-300 text-amber-700 bg-amber-50' :
                        'border-green-300 text-green-700 bg-green-50'
                      }`}>
                        {inv.confidence || 'N/A'}
                      </Badge>
                    </td>
                    <td className="px-5 py-3"><ArrowRight className="h-3 w-3 text-slate-300" /></td>
                  </tr>
                ))}
                {attention.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-400">No items requiring attention</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
